import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';

const defaultTitle = { title: 'BACK', param: {} } as const;

export interface GoBackText {
  title: string;
  param?: Record<string, string>;
}

/**
 * Internal service for managing go-back button state.
 *
 * @internal This service should ONLY be used by layout.effect.ts
 * Use GoBackService for read-only access to the go-back state.
 */
@Injectable({
  providedIn: 'root',
})
export class GoBackWriteService {
  private readonly route = new ReplaySubject<string | null>(1);
  private readonly queryParams = new ReplaySubject<Record<string, string> | null>(1);
  private readonly text = new BehaviorSubject<GoBackText>(defaultTitle);
  private readonly clicked = new Subject<void>();

  readonly route$: Observable<string | null> = this.route.asObservable();
  readonly queryParams$: Observable<Record<string, string> | null> =
    this.queryParams.asObservable();
  readonly text$: Observable<GoBackText> = this.text.asObservable();
  readonly clicked$: Observable<void> = this.clicked.asObservable();

  setRoute(route: string) {
    const [routeName, params] = route.split('?', 2);
    this.route.next('/' + routeName);
    this.queryParams.next(params ? Object.fromEntries(new URLSearchParams(params)) : null);
  }

  clearRoute() {
    this.route.next(null);
    this.queryParams.next(null);
  }

  setTitle(title: string, param = {}) {
    this.text.next({ title, param });
  }

  clearText() {
    this.text.next(defaultTitle);
  }

  clearQueryParams() {
    this.queryParams.next(null);
  }

  click() {
    this.clicked.next();
  }

  clear(): void {
    this.clearRoute();
    this.clearText();
    this.clearQueryParams();
  }
}
